import { RotateCcw } from 'lucide-react';
import { StageProgressBar } from '@/components/ui/stage-progress';
import {
  useRetryDocumentProcessingMutation,
  type DocumentProcessingItem,
} from '@/lib/api/document-processing';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils/cn';
import { processingView } from './processing-logic';

/**
 * A document's processing status under its name (T-0159): the stage with a
 * real percentage and count while it runs, and on failure the stage, a
 * one-line reason in plain words and Retry (re-index or re-read, through the
 * existing endpoints). Used by the wizard's baseline-docs rows and the
 * Documents screen's Ingestion rail.
 */
export interface DocumentProcessingStatusProps {
  engagementId: string;
  document: DocumentProcessingItem;
  /** The bar's track: `sm` in list rows, `md` in panels. */
  size?: 'sm' | 'md';
  className?: string;
}

export function DocumentProcessingStatus({
  engagementId,
  document,
  size = 'sm',
  className,
}: DocumentProcessingStatusProps) {
  const view = processingView(document);
  const retry = useRetryDocumentProcessingMutation(engagementId);
  const retryError =
    retry.error instanceof ApiError
      ? retry.error.message
      : retry.error
        ? 'The retry could not be started.'
        : null;

  return (
    <div className={cn('min-w-0', className)}>
      {view.tone === 'failed' ? (
        <div className="text-meta font-medium text-posture-critical">{view.line}</div>
      ) : (
        <StageProgressBar
          percent={view.percent}
          label={view.line}
          ariaLabel={`${view.chip} ${document.name}`}
          tone={view.tone === 'done' ? 'done' : 'working'}
          size={size}
        />
      )}
      {view.reason ? (
        <p
          className={cn(
            'mt-0.5 text-meta',
            view.tone === 'failed' ? 'text-ink-700' : 'text-ink-500',
          )}
          title={view.detail ?? undefined}
        >
          {view.reason}
        </p>
      ) : null}
      {view.retry ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => retry.mutate({ documentId: document.id, action: view.retry! })}
            disabled={retry.isPending}
            aria-label={`Retry ${view.retryWhat} for ${document.name}`}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta font-medium text-ink-700',
              'hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-60',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
            )}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {retry.isPending ? 'Retrying…' : 'Retry'}
          </button>
          {retryError ? (
            <span role="alert" className="text-meta text-posture-critical">
              {retryError}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
